import { z } from "zod"

/**
 * Contract for pre-baked WebGL mockup bundles (the "static-region-v1"
 * transport produced by the mockup-3d-renderer pipeline).
 *
 * A bundle is authored per catalog product and holds, per camera view
 * ("angle"), a delivery-graded static PNG of the whole frame plus raw
 * float data cropped to the label box. The browser engine composites the
 * user's label artwork into that box:
 *
 *   out = plate + albedo * linear + albedo^2 * quadratic
 *
 * Two halves travel together and are validated separately:
 *  - the authored manifest (product identity, angles, dieline), and
 *  - the generated pass index (which files a bake actually produced).
 *
 * Views may additionally ship a layered split of the same graded render
 * (backgroundImage + productImage) that powers the in-browser mockup
 * editor; see mockupPassIndexAngleSchema.
 *
 * Schemas parse with unknown-key stripping: bakes carry extra provenance
 * (payload sizes, bake settings, conversion journal data) that the runtime
 * never reads, and the API should not ship it to the browser.
 */

export const MOCKUP_PASS_SCHEMAS = [
  "static-region-v1",
  "legacy-full-frame",
] as const

export const MOCKUP_PASS_SPACES = ["linear-scene", "display-srgb"] as const

export type MockupPassSchema = (typeof MOCKUP_PASS_SCHEMAS)[number]
export type MockupPassSpace = (typeof MOCKUP_PASS_SPACES)[number]

/** An index written before the schema split is a legacy full-frame one. */
export const DEFAULT_MOCKUP_PASS_SCHEMA: MockupPassSchema = "legacy-full-frame"
/** An index written before converted bundles existed is a native bake. */
export const DEFAULT_MOCKUP_PASS_SPACE: MockupPassSpace = "linear-scene"

const integerPair = z.tuple([z.number().int(), z.number().int()])

const numberPair = z.tuple([z.number(), z.number()])

export const mockupDielineSchema = z.object({
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
  dpi: z.number().positive(),
  /** Artwork pixel size the engine letterboxes uploads onto. */
  expectedPixels: integerPair.optional(),
  /** Label trim per the product SKU database, [width, height] inches. */
  trimInches: numberPair.optional(),
  sourceMesh: z.string().optional(),
})

export const mockupLabelSurfaceBindingSchema = z.object({
  objectName: z.string(),
  uvLayer: z.string(),
  /** Binds this surface to an upload slot; distinct slots enable variants. */
  artworkSlot: z.string(),
})

export const mockupManifestAngleSchema = z.object({
  id: z.string().min(1),
  sceneName: z.string(),
  cameraName: z.string().optional(),
  /** Angle directory under the bundle root; prefixes every runtime file. */
  path: z.string().min(1),
  /** True when the angle has no label surface and ships as a finished plate. */
  static: z.boolean(),
  labelSurfaces: z.array(mockupLabelSurfaceBindingSchema).optional(),
})

export const mockupBundleManifestSchema = z.object({
  version: z.literal(2),
  productId: z.string().min(1),
  sceneType: z.string(),
  assetRoot: z.string(),
  /** Generated pass index filename, relative to assetRoot. */
  passIndex: z.string(),
  passModel: z.string().optional(),
  artworkSlots: z
    .array(z.object({ id: z.string(), description: z.string().optional() }))
    .optional(),
  labelSurface: z.object({ dieline: mockupDielineSchema.optional() }).optional(),
  angles: z.array(mockupManifestAngleSchema).min(1),
  /** Identity in the product SKU database, written by the Blender addon. */
  catalog: z
    .object({
      sku: z.string().optional(),
      packageId: z.string().optional(),
      productName: z.string().optional(),
      labelId: z.string().optional(),
      dielineSource: z.string().optional(),
    })
    .optional(),
})

export type MockupBundleManifest = z.infer<typeof mockupBundleManifestSchema>
export type MockupManifestAngle = z.infer<typeof mockupManifestAngleSchema>
export type MockupDieline = z.infer<typeof mockupDielineSchema>

export const mockupPassEntrySchema = z.object({
  /** What the browser loads, relative to the angle directory. */
  runtime: z.string().min(1),
  /** 8-bit sRGB preview for debug UIs only. */
  preview: z.string().optional(),
  encoding: z.enum(["rgba16f", "uv16-rgba8", "r8"]),
  colorSpace: z.enum(["linear-rec709", "srgb", "data"]),
  source: z.string().optional(),
})

export const mockupStaticImageSchema = z.object({
  runtime: z.string().min(1),
  encoding: z.enum(["srgb8", "srgb8a"]),
  colorSpace: z.literal("srgb"),
  alpha: z.boolean().optional(),
  source: z.string().optional(),
})

export const mockupRegionSchema = z.object({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  width: z.number().int().min(1),
  height: z.number().int().min(1),
  margin: z.number().int().min(0).optional(),
  coverageThreshold: z.number().optional(),
  labelPixels: z.number().int().optional(),
})

/**
 * Per-view delivery exposure. The engine only consumes `exposureEv`
 * (linear gain 2^exposureEv applied ahead of the display transform);
 * the rest is authored/solved provenance.
 */
export const mockupDisplayCalibrationSchema = z.object({
  mode: z.enum(["auto", "manual"]).optional(),
  exposureEv: z.number().optional(),
  exposureOffsetEv: z.number().optional(),
  note: z.string().optional(),
})

export type MockupDisplayCalibration = z.infer<
  typeof mockupDisplayCalibrationSchema
>

export const mockupDisplayGradeSchema = z.object({
  /** Baked OCIO display LUT strip, relative to the bundle root. */
  lut: z.string().min(1),
  /** Photoshop finishing-curve rows, relative to the bundle root. */
  curves: z.string().min(1),
  cubeSize: z.number().int().min(2),
  shaper: z.object({ maximum: z.number(), offset: z.number() }),
  ocio: z.string().optional(),
})

/**
 * Optional generated-substance crop (powder SKUs). `static.png` already
 * carries the generated powder, but the label box is re-blitted from
 * passes rendered with the pipeline's magenta powder proxy in frame — so
 * the crop (delivery pixels with coverage as alpha, cut to the powder ∩
 * label-box intersection) must be redrawn at (x, y) after the blit, or
 * the composite undoes the powder inside the box and leaks proxy pixels.
 */
export const mockupPowderOverlaySchema = z.object({
  /** Crop PNG, relative to the angle directory. */
  runtime: z.string().min(1),
  /** Full-frame position the crop is redrawn at, in pixels. */
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  width: z.number().int().min(1).optional(),
  height: z.number().int().min(1).optional(),
})

export type MockupPowderOverlay = z.infer<typeof mockupPowderOverlaySchema>

export const mockupPassIndexAngleSchema = z.object({
  passSchema: z.enum(MOCKUP_PASS_SCHEMAS).optional(),
  passSpace: z.enum(MOCKUP_PASS_SPACES).optional(),
  static: z.boolean(),
  resolution: integerPair,
  staticImage: mockupStaticImageSchema.optional(),
  /**
   * Layered delivery (the mockup editor's transport): the same graded render
   * split into a scene backdrop without the product and the product cut out
   * on transparency at the neutral (no-label) state. Both full-frame at
   * `resolution`, both graded identically to `staticImage` — flattening
   * product over background reproduces static.png. The region passes stay
   * full-scene: the editor masks the label composite by the product cutout's
   * alpha, so no layer-specific pass bake exists. Always paired.
   */
  backgroundImage: mockupStaticImageSchema.optional(),
  productImage: mockupStaticImageSchema.optional(),
  region: mockupRegionSchema.optional(),
  passes: z.record(z.string(), mockupPassEntrySchema).optional(),
  displayCalibration: mockupDisplayCalibrationSchema.optional(),
  quadraticModel: z.boolean().optional(),
  powderOverlay: mockupPowderOverlaySchema.optional(),
})

export type MockupPassIndexAngle = z.infer<typeof mockupPassIndexAngleSchema>

/**
 * Response passes a composited (non-static) static-region-v1 view cannot
 * run without. `quadratic` is legitimately optional (a converted bundle
 * has no interreflection term) and `productCoverage` only drives the
 * grade split, which a display-srgb bundle does not run.
 */
const REQUIRED_COMPOSITE_PASSES = ["plate", "linear", "uv", "coverage"] as const

export const mockupPassIndexSchema = z
  .object({
    product: z.string(),
    formula: z.string(),
    passSchema: z.enum(MOCKUP_PASS_SCHEMAS).optional(),
    passSpace: z.enum(MOCKUP_PASS_SPACES).optional(),
    displayGrade: mockupDisplayGradeSchema.nullable().optional(),
    angles: z.record(z.string(), mockupPassIndexAngleSchema),
  })
  .superRefine((index, ctx) => {
    const passSpace = index.passSpace ?? DEFAULT_MOCKUP_PASS_SPACE
    // A native bake with no grade assets is a stale bake; a display-srgb
    // bundle carries the grade in its own pixels and ships none.
    if (passSpace === "linear-scene" && !index.displayGrade) {
      ctx.addIssue({
        code: "custom",
        path: ["displayGrade"],
        message: "A linear-scene bundle requires displayGrade assets.",
      })
    }
    for (const [viewId, angle] of Object.entries(index.angles)) {
      if (!angle.staticImage && resolvePassSchema(index, angle) === "static-region-v1") {
        ctx.addIssue({
          code: "custom",
          path: ["angles", viewId, "staticImage"],
          message: "A static-region-v1 view requires a staticImage.",
        })
      }
      // Layered delivery is all-or-nothing per view: a lone half means the
      // bake was interrupted or the inventory is stale.
      if (Boolean(angle.backgroundImage) !== Boolean(angle.productImage)) {
        ctx.addIssue({
          code: "custom",
          path: ["angles", viewId, angle.backgroundImage ? "productImage" : "backgroundImage"],
          message:
            "Layered views require backgroundImage and productImage together.",
        })
      }
      if (angle.static) continue
      if (!angle.region) {
        ctx.addIssue({
          code: "custom",
          path: ["angles", viewId, "region"],
          message: "A composited view requires a region box.",
        })
      }
      for (const pass of REQUIRED_COMPOSITE_PASSES) {
        if (!angle.passes?.[pass]) {
          ctx.addIssue({
            code: "custom",
            path: ["angles", viewId, "passes", pass],
            message: `A composited view requires the '${pass}' pass.`,
          })
        }
      }
    }
  })

export type MockupPassIndex = z.infer<typeof mockupPassIndexSchema>

export const resolvePassSchema = (
  index: Pick<MockupPassIndex, "passSchema">,
  angle?: Pick<MockupPassIndexAngle, "passSchema">,
): MockupPassSchema =>
  angle?.passSchema ?? index.passSchema ?? DEFAULT_MOCKUP_PASS_SCHEMA

export const resolvePassSpace = (
  index: Pick<MockupPassIndex, "passSpace">,
  angle?: Pick<MockupPassIndexAngle, "passSpace">,
): MockupPassSpace =>
  angle?.passSpace ?? index.passSpace ?? DEFAULT_MOCKUP_PASS_SPACE

/**
 * One stored bundle file. `path` is bundle-root-relative in the layout the
 * pipeline writes: `<angle.path>/<pass.runtime>` for per-view files and the
 * displayGrade paths verbatim for shared grade assets.
 */
export const mockupBundleFileSchema = z.object({
  path: z.string().min(1),
  url: z.string().min(1),
  sha256: z.string().optional(),
  bytes: z.number().int().optional(),
})

export type MockupBundleFile = z.infer<typeof mockupBundleFileSchema>

/** Resolved bundle the API hands the browser engine. */
export type MockupBundlePayload = {
  manifest: MockupBundleManifest
  passIndex: MockupPassIndex
  files: MockupBundleFile[]
}

/** `bundle.json` inventory written by the pipeline's bundle export. */
export const mockupBundleInventorySchema = z.object({
  files: z.array(
    z.object({
      path: z.string().min(1),
      bytes: z.number().int().min(0),
      sha256: z.string().length(64),
    }),
  ),
})

export type MockupBundleInventory = z.infer<typeof mockupBundleInventorySchema>

export const buildMockupBundleFileUrlMap = (
  files: readonly MockupBundleFile[],
): Record<string, string> =>
  Object.fromEntries(files.map((file) => [file.path, file.url]))

/**
 * Every bundle-root-relative file path the runtime needs to composite the
 * bundle: per-view static images and runtime passes, plus shared grade
 * assets. Debug previews are intentionally excluded — the product UI never
 * shows them and they would only inflate the payload.
 */
export const collectMockupBundleRuntimePaths = (
  manifest: Pick<MockupBundleManifest, "angles">,
  passIndex: MockupPassIndex,
): string[] => {
  const paths: string[] = []
  for (const angle of manifest.angles) {
    const baked = passIndex.angles[angle.id]
    if (!baked) continue
    if (baked.staticImage) {
      paths.push(`${angle.path}/${baked.staticImage.runtime}`)
    }
    if (baked.powderOverlay) {
      paths.push(`${angle.path}/${baked.powderOverlay.runtime}`)
    }
    // Layered images apply to static views too (a finished plate can still
    // be re-backgrounded in the mockup editor).
    if (baked.backgroundImage) {
      paths.push(`${angle.path}/${baked.backgroundImage.runtime}`)
    }
    if (baked.productImage) {
      paths.push(`${angle.path}/${baked.productImage.runtime}`)
    }
    if (baked.static) continue
    for (const pass of Object.values(baked.passes ?? {})) {
      paths.push(`${angle.path}/${pass.runtime}`)
    }
  }
  if (passIndex.displayGrade) {
    paths.push(passIndex.displayGrade.lut, passIndex.displayGrade.curves)
  }
  return paths
}

export type MockupBundleValidationIssue =
  | { kind: "missingFile"; path: string }
  | { kind: "missingAngle"; viewId: string }

/**
 * Cross-checks the three stored pieces of a bundle: every angle the
 * manifest declares must exist in the pass index, and every runtime file
 * the index declares must have a stored file. Used by the upload script
 * before writing to Sanity and by the API before serving a bundle.
 */
export const validateMockupBundleCompleteness = (
  manifest: MockupBundleManifest,
  passIndex: MockupPassIndex,
  availablePaths: ReadonlySet<string>,
): MockupBundleValidationIssue[] => {
  const issues: MockupBundleValidationIssue[] = []
  for (const angle of manifest.angles) {
    if (!passIndex.angles[angle.id]) {
      issues.push({ kind: "missingAngle", viewId: angle.id })
    }
  }
  for (const path of collectMockupBundleRuntimePaths(manifest, passIndex)) {
    if (!availablePaths.has(path)) {
      issues.push({ kind: "missingFile", path })
    }
  }
  return issues
}

export type MockupLabelDimensionCheck = {
  valid: boolean
  /** Relative aspect-ratio deviation from the dieline, e.g. 0.013 = 1.3%. */
  aspectDelta: number
  /** Whether pixel dimensions are within the size tolerance of the dieline. */
  closeToExpectedPixels: boolean
}

/** Artwork whose aspect deviates more than this from the dieline is rejected. */
export const MOCKUP_LABEL_ASPECT_TOLERANCE = 0.02
/** Pixel-size deviation beyond this is fitted with padding rather than rejected. */
export const MOCKUP_LABEL_PIXEL_TOLERANCE = 0.1

/**
 * Pure dimension check for label artwork against the dieline's expected
 * pixel size. Aspect is a hard gate (a stretched label would visibly warp
 * on the package); absolute pixel size is only advisory because the engine
 * letterboxes proportionally.
 */
export const checkMockupLabelDimensions = (
  width: number,
  height: number,
  expectedPixels: readonly [number, number],
): MockupLabelDimensionCheck => {
  const [expectedWidth, expectedHeight] = expectedPixels
  const aspectDelta = Math.abs(
    (width / height / (expectedWidth / expectedHeight)) - 1,
  )
  const widthDelta = Math.abs(width / expectedWidth - 1)
  const heightDelta = Math.abs(height / expectedHeight - 1)
  return {
    valid: aspectDelta <= MOCKUP_LABEL_ASPECT_TOLERANCE,
    aspectDelta,
    closeToExpectedPixels:
      widthDelta <= MOCKUP_LABEL_PIXEL_TOLERANCE &&
      heightDelta <= MOCKUP_LABEL_PIXEL_TOLERANCE,
  }
}
